import React, { useState } from 'react';
import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, List, ListOrdered, Link, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useForm } from 'react-hook-form';

interface EmailFormattingToolbarProps {
  onFormat: (command: string, value?: string) => void;
}

export function EmailFormattingToolbar({ onFormat }: EmailFormattingToolbarProps) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  const linkForm = useForm<{ url: string }>({
    defaultValues: { url: '' }
  });

  const imageForm = useForm<{ url: string }>({
    defaultValues: { url: '' }
  });

  const handleLinkSubmit = (data: { url: string }) => {
    onFormat('createLink', data.url);
    setLinkDialogOpen(false);
    linkForm.reset();
  };

  const handleImageSubmit = (data: { url: string }) => {
    onFormat('insertImage', data.url);
    setImageDialogOpen(false);
    imageForm.reset();
  };

  return (
    <div className="flex items-center gap-1 p-2 bg-muted/50 rounded-lg border border-border/50">
      {/* Text Formatting */}
      <ToggleGroup type="multiple" className="gap-1">
        <ToggleGroupItem
          value="bold"
          onClick={() => onFormat('bold')}
          className="h-8 w-8 p-0"
          aria-label="Bold"
        >
          <Bold className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="italic"
          onClick={() => onFormat('italic')}
          className="h-8 w-8 p-0"
          aria-label="Italic"
        >
          <Italic className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="underline"
          onClick={() => onFormat('underline')}
          className="h-8 w-8 p-0"
          aria-label="Underline"
        >
          <Underline className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>

      <div className="w-px h-6 bg-border/50 mx-1" />

      {/* Alignment */}
      <ToggleGroup type="single" className="gap-1">
        <ToggleGroupItem
          value="left"
          onClick={() => onFormat('justifyLeft')}
          className="h-8 w-8 p-0"
          aria-label="Align Left"
        >
          <AlignLeft className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="center"
          onClick={() => onFormat('justifyCenter')}
          className="h-8 w-8 p-0"
          aria-label="Align Center"
        >
          <AlignCenter className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="right"
          onClick={() => onFormat('justifyRight')}
          className="h-8 w-8 p-0"
          aria-label="Align Right"
        >
          <AlignRight className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>

      <div className="w-px h-6 bg-border/50 mx-1" />

      {/* Lists */}
      <ToggleGroup type="single" className="gap-1">
        <ToggleGroupItem
          value="unordered"
          onClick={() => onFormat('insertUnorderedList')}
          className="h-8 w-8 p-0"
          aria-label="Bullet List"
        >
          <List className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="ordered"
          onClick={() => onFormat('insertOrderedList')}
          className="h-8 w-8 p-0"
          aria-label="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>

      <div className="w-px h-6 bg-border/50 mx-1" />

      {/* Links and Images */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Link className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
          </DialogHeader>
          <form onSubmit={linkForm.handleSubmit(handleLinkSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="url">URL</Label>
              <Input
                id="url"
                placeholder="https://example.com"
                {...linkForm.register('url', { required: true })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setLinkDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Insert Link</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Image className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Image</DialogTitle>
          </DialogHeader>
          <form onSubmit={imageForm.handleSubmit(handleImageSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="imageUrl">Image URL</Label>
              <Input
                id="imageUrl"
                placeholder="https://example.com/image.jpg"
                {...imageForm.register('url', { required: true })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setImageDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Insert Image</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}